import React, { FC } from "react";
import format from "date-fns/format";
import nb from "date-fns/locale/nb";

import { StreamLineIcon } from "../../../../../SvgIcon/StreamLineIcon";
import { useInsuranceLabels } from "../../../../Utils/Context/labelsContext";

import { InsuranceFormState } from "../../Utils/insuranceForm";
import { InsuranceFormSubmissionStatus } from "../../../../Utils/types";

import { BookingConfirmationPart } from "./BookingConfirmationPart";
import classNames from "classnames";

interface BookingConfirmationSummaryProps {
  formState: InsuranceFormState;
  status?: InsuranceFormSubmissionStatus;
}

export const BookingConfirmationSummary: FC<BookingConfirmationSummaryProps> = (
  props
) => {
  const { formState, status } = props;
  const { date, filters, notes, ssn, caseNumber, referenceFiles } = formState;

  const { headings, inputs } = useInsuranceLabels();

  const statusModifiers = () => {
    switch (status) {
      case "success":
        return {
          header: headings.bookedModalTitle,
          subHeader: headings.bookedModalSubTitle,
          className: "bookingConfirmationSummary__content--success",
        };
      case "error":
        return {
          header: headings.errorModalTitle,
          subHeader: headings.errorModalSubTitle,
          className: "bookingConfirmationSummary__content--error",
        };
      case "loading":
      case "initial":
      default:
        return {
          header: headings.confirmModalTitle,
          subHeader: headings.confirmModalSubTitle,
          className: "bookingConfirmationSummary__content--summary",
        };
    }
  };

  const renderFirstColumn = () => (
    <div className="bookingConfirmationSummary__contentColumn">
      <BookingConfirmationPart
        heading={inputs.servicesLabel}
        body={filters?.service && <span>{filters?.service.bookingName}</span>}
      />
      <BookingConfirmationPart
        heading={headings.specialistHeading}
        body={filters?.doctor && <span>{filters?.doctor.name}</span>}
      />
      <BookingConfirmationPart
        heading={inputs.caseNumberLabel}
        body={caseNumber && <span>{caseNumber}</span>}
      />
      <BookingConfirmationPart
        heading={inputs.referenceLabel}
        body={
          !!referenceFiles.length && (
            <span className="bookingConfirmationSummary__textWithIcon">
              <span className="bookingConfirmationSummary__icon">
                <StreamLineIcon iconName="s-attachment" />
              </span>
              {referenceFiles.map((file) => file.name).join(", ")}
            </span>
          )
        }
      />
    </div>
  );

  const renderSecondColumn = () => (
    <div className="bookingConfirmationSummary__contentColumn">
      <BookingConfirmationPart
        heading={inputs.dateLabel}
        body={
          date && (
            <span>
              {format(date, "dd-MM-y", { locale: nb })}
              <br />
              {format(date, "p", { locale: nb })}
            </span>
          )
        }
      />
      <BookingConfirmationPart
        heading={inputs.locationLabel}
        body={
          !!filters?.locations?.length &&
          filters.locations.map((location) => location.name).join(", ")
        }
      />
      <BookingConfirmationPart
        heading={inputs.ssnLabel}
        body={ssn && <span>{ssn}</span>}
      />
    </div>
  );

  const renderRow = () => (
    <>
      <div className="bookingConfirmationSummary__contentRow">
        <BookingConfirmationPart
          heading={inputs.notesLabel}
          body={notes && <i>{notes}</i>}
        />
      </div>
      <div className="bookingConfirmationSummary__contentRow">
        <BookingConfirmationPart
          heading={headings.practicalInfoHeading}
          body={
            filters?.service?.practicalInfoInsurance && (
              <div
                dangerouslySetInnerHTML={{
                  __html: filters?.service.practicalInfoInsurance,
                }}
              />
            )
          }
        />
      </div>
    </>
  );

  const { header, subHeader, className } = statusModifiers();

  return (
    <div className="bookingConfirmationSummary">
      <h2
        className="bookingConfirmationSummary__header"
        id="bookingConfirmation-header"
      >
        {header}
      </h2>
      <h3 className="bookingConfirmationSummary__subHeader">{subHeader}</h3>
      <div
        className={classNames("bookingConfirmationSummary__content", className)}
      >
        <div className="bookingConfirmationSummary__contentColumns">
          {renderFirstColumn()}
          {renderSecondColumn()}
        </div>
        {renderRow()}
      </div>
    </div>
  );
};
